// @ts-check
const { Decimal } = require('decimal.js');
const { v4: uuidv4 } = require('uuid');
const {
  feeOptionsSchema,
  paymentTypeSchema,
  feeSchema,
  feeConfigSchema,
  feeArraySchema,
  baseAmountSchema,
  referenceNumberSchema,
  stateSchema,
} = require('../validation/feeServiceSchemas.js');
const { getPercentDecimalPlaces, formatPotentialNaN } = require('../utils/feeService.utils.js');

/**
 * @description Represents a value with calculated and stored representations for use on fee objects
 *
 * @param {string} value
 * @param {FeeType} type
 */
function Value(value, type) {
  let fVal = new Decimal(value).toFixed(2);
  if (type === '%') fVal = getPercentDecimalPlaces(value);
  this.calculated = fVal;
  this.stored = fVal;
}

/** @type {FeeConfig} */
class FeeConfiguration {
  /**
   * @param {FeeConfigInput} feeConfigInput
   * @param {SurchargeByStateOptions | {}} surchargeByStateOptions
   * @param {State | ""} state - US states
   */
  constructor(feeConfigInput, surchargeByStateOptions = {}, state = '') {
    /**
     * @param {string} value
     * @param {FeeType} type
     */
    const { cardUpcharge, tax, lateFee, surcharge } = feeConfigInput;

    this.cardUpcharge = {
      ...cardUpcharge,
      name: 'cardUpcharge',
      value: new Value(cardUpcharge.value, cardUpcharge.type),
    };
    this.tax = { ...tax, name: 'tax', value: new Value(tax.value, tax.type) };
    this.lateFee = { ...lateFee, name: 'lateFee', value: new Value(lateFee.value, lateFee.type) };
    this.surcharge = { ...surcharge, name: 'surcharge', value: new Value(surcharge.value, surcharge.type) };

    // Ensure surchargeByStateOptions is always an object with expected properties
    const surchargeOptions = {
      enabled: false,
      rates: {},
      ...surchargeByStateOptions,
    };

    if (surchargeOptions.enabled && surchargeOptions.rates && state) {
      const selectedRate = surchargeOptions.rates[state] || {};
      // Apply surcharge of 0 value, infer type from existing surcharge setting
      if (
        selectedRate.application &&
        selectedRate.amount &&
        selectedRate.type &&
        !new Decimal(selectedRate.amount).isNaN()
      ) {
        this.surcharge.value = new Value(selectedRate.amount, feeConfigInput.surcharge.type);
        this.surcharge.type = selectedRate.type;
      } else {
        this.surcharge.value = new Value('0', feeConfigInput.surcharge.type);
      }
    }
  }

  /** @type {Fee} */
  surcharge;

  /** @type {Fee} */
  tax;

  /** @type {Fee} */
  lateFee;

  /** @type {Fee} */
  cardUpcharge;
}

class FeeService {
  /**
   * @constructor
   * @param {import('winston').Logger | MockLogger} logger - winston logger instance
   */
  constructor(logger) {
    this.logger = logger;
  }

  /**
   * @description Calculates fees based on feeConfig and paymentType.
   *
   * When paymentType is an empty string and options.dualPricing is true, the method will
   * delegate to getDualPricingCalculatedFees.
   *
   * Otherwise, it builds a fee program from feeConfig following these rules:
   * - surcharge is applied only if paymentType === 'credit'.
   *
   * Calculation is performed similarly to getDualPricingCalculatedFees, and the result is returned
   * in the CalculationViewResult shape.
   *
   * Fee Options:
   * - dualPricing: Handles cash and card price logic. If viewAllPrices is FALSE, then the logic assumes this is a
   * payment. However, paymentType can be omitted in which case 'debit' is the default view on the frontend.
   * - viewAllPrices: This option, when paired with dualPricing, changes the shape of the return object
   * to reflect cash and card prices and fees in one response.
   * - taxLast: This option causes tax to be applied last and to the running total rather than the base amount.
   * - lateFeeEffectiveDate: REQUIRED when lateFee is enabled for it to take effect. A late fee's effective
   * date must be in the past for it to take effect.
   *
   * @public
   * @param {string} baseAmount
   * @param {FeeConfigInput} feeConfigInput
   * @param {FeeOptions} options
   * @param {PaymentType | "card"} [paymentType=""]
   * @param {State | ""} [state=""]
   * @returns {CalculationViewResult | DualPricingViewResult}
   * @throws {Error}
   */
  getCalculatedFees(baseAmount, feeConfigInput, options = {}, paymentType = '', state = '') {
    const referenceNumber = uuidv4();

    // #region VALIDATION
    /** @type {Array<Error | undefined>} */
    const validationErrors = [];
    paymentType = paymentType?.toLowerCase();

    if (paymentType === 'card') {
      paymentType = 'debit'; // default card payment type to debit for compatibility with unknown creditOrDebit cards.
    }

    const { error: paymentTypeError } = paymentTypeSchema.validate(paymentType);
    validationErrors.push(paymentTypeError);

    if (state) {
      const { error: stateError } = stateSchema.validate(state);
      validationErrors.push(stateError);
    }

    // Handle potential NaN intermediary input values like '5.' as '5.00' or '.' as '0.00'
    baseAmount = formatPotentialNaN(baseAmount);

    const { error: baseAmountError } = baseAmountSchema.validate(baseAmount);
    validationErrors.push(baseAmountError);

    const { error: optionsError } = feeOptionsSchema.validate(options);
    validationErrors.push(optionsError);

    const { error: feeConfigError } = feeConfigSchema.validate(feeConfigInput);
    validationErrors.push(feeConfigError);

    validationErrors.forEach((e) => {
      if (e instanceof Error) {
        this.logger.error({
          logID: 22103,
          message: e.message,
          referenceNumber,
        });
        throw e;
      }
    });
    // #endregion

    // Map fees to new 'value' property shape {calculated: string, stored: string}
    /** @type {FeeConfig} */
    const feeConfig = new FeeConfiguration(feeConfigInput, options.surchargeByState, state);

    // View Only only has an effect if dualPricing is true.
    if (options.viewAllPrices && options.dualPricing) {
      return this.getDualPricingCalculatedFees(
        baseAmount,
        feeConfigInput,
        options,
        paymentType,
        state,
        referenceNumber,
      );
    }

    // Build fee program based on feeConfig and options.
    /** @type {Fee[]} */
    const feeProgram = [];

    const showCashDiscount = !options.dualPricing && (paymentType === 'ach' || paymentType === 'cash');

    /** @type {Fee} */
    const cardUpcharge = this._getCardUpchargeDisplayData(
      feeConfig.cardUpcharge,
      options,
      showCashDiscount,
      paymentType,
    );
    feeProgram.push(cardUpcharge);

    // Dual Pricing OFF, Cash Discount enabled, apply negative value of cash discount for display
    if (feeConfig.cardUpcharge.enabled && showCashDiscount) {
      const negativeString = `-${feeConfig.cardUpcharge.value.calculated}`;
      const calculated =
        feeConfig.cardUpcharge.type === '$'
          ? new Decimal(negativeString).toFixed(2)
          : getPercentDecimalPlaces(negativeString);

      /** @type {Fee} */
      const cashDiscount = {
        name: 'cashDiscount',
        display: true,
        type: feeConfig.cardUpcharge.type,
        value: {
          calculated,
          stored: feeConfig.cardUpcharge.value.stored,
        },
      };

      feeConfig['cashDiscount'] = cashDiscount;
      feeProgram.push(cashDiscount);
    }

    // Apply surcharge only if paymentType is 'credit'
    /** @type {Fee} */
    const surcharge = { ...feeConfig.surcharge, name: 'surcharge', display: !!feeConfig.surcharge.enabled };
    if (!feeConfig.surcharge.enabled || paymentType !== 'credit') {
      feeConfig.surcharge.value.calculated = '0.00';
    }
    feeProgram.push(surcharge);

    /** @type {Fee} */
    const lateFee = this._getLateFeeDisplayData(feeConfig.lateFee, options);
    feeProgram.push(lateFee);

    const tax = this._getTaxDisplayData(feeConfig.tax, options);
    feeProgram.push(tax);

    // Calculate fees
    const calculationResult = this._calculate(baseAmount, feeProgram, referenceNumber, options, paymentType);

    if (!calculationResult?.fees) {
      const erMsg = 'Calculation result is missing fees data.';
      this.logger.error({ logID: 21967, referenceNumber, message: erMsg });
      throw new Error(erMsg);
    }

    // Return the single-calculatedulation result in CalculationViewResult shape.
    /** @type {CalculationViewResult} */
    const result = {
      totalAmount: calculationResult.totalAmount,
      subTotal: calculationResult.subTotal,
      baseAmount: calculationResult.baseAmount,
      fees: calculationResult.fees,
      paymentType,
      options,
      state,
      referenceNumber,
    };

    return result;
  }

  /**
   * @description business logic for tax display data
   *
   * @param {Fee} tax
   * @param {FeeOptions} options
   * @returns {Fee}
   */
  _getTaxDisplayData(tax, options) {
    /** @type {Fee} */
    const fee = {
      ...tax,
      name: 'tax',
      display: !!tax.enabled,
    };

    if (options.dualPricing) options['taxLast'] = true; // Tax must be applied last if dualPricing is true
    if (!fee.enabled) {
      fee.value.calculated = '0.00';
    }
    return fee;
  }

  /**
   * @description business logic for late fee display data
   *
   * @param {Fee} lateFee
   * @param {FeeOptions} options
   * @returns {Fee}
   */
  _getLateFeeDisplayData(lateFee, options) {
    /** @type {Fee} */
    const fee = {
      ...lateFee,
      display: false,
      name: 'lateFee',
    };
    if (fee.enabled && options.lateFeeEffectiveDate) {
      const effectiveDate = new Date(options.lateFeeEffectiveDate);
      fee.display = true;
      const now = new Date();
      if (effectiveDate >= now) {
        fee.value.calculated = '0.00';
      }
    } else {
      fee.value.calculated = '0.00';
    }

    return fee;
  }

  /**
   * @description business logic for card upcharge display data
   *
   * @param {Fee} cardUpcharge
   * @param {FeeOptions} options
   * @param {boolean} showCashDiscount
   * @param {PaymentType} [paymentType=""]
   * @returns {Fee}
   */
  _getCardUpchargeDisplayData(cardUpcharge, options, showCashDiscount, paymentType = '') {
    /** @type {Fee} */
    const fee = {
      ...cardUpcharge,
      name: 'cardUpcharge',
      display: !!cardUpcharge.enabled,
    };

    if (options.dualPricing) fee.display = false;
    const cardPaymentTypes = ['credit', 'debit', ''];

    if (!fee.enabled) {
      fee.value.calculated = '0.00';
    }

    // With dual pricing off, we want to see the SVC (cash discount) added and subracted as ACH discount
    if (!cardPaymentTypes.includes(paymentType) && !showCashDiscount) {
      fee.value.calculated = '0.00';
    }

    return fee;
  }
  /**
   * @description Takes in base amount, fee config data and maps
   * input to this._calculate(). Output is a frontend-ready object custom to
   * each with 'display' flags for each fee that the view should
   * render.
   *
   * Note: This function does not take payment type as it is for getting VIEWS only.
   *
   * @public
   * @param {string} baseAmount
   * @param {FeeConfigInput} feeConfigInput
   * @param {FeeOptions} options
   * @param {PaymentType | "card"} paymentType
   * @param {State | ""} [state=""]
   * @param {string} [referenceNumber]
   * @returns {DualPricingViewResult | DualPricingViewResult }
   */
  getDualPricingCalculatedFees(
    baseAmount,
    feeConfigInput,
    options = {},
    paymentType = '',
    state = '',
    referenceNumber = '',
  ) {
    referenceNumber = referenceNumber || uuidv4(); // Reference number may be provided by parent function call
    options['dualPricing'] = true; // In the context of this method, dualPricing must be true. And the output 'options' will reflect this.

    paymentType = paymentType?.toLowerCase();
    if (paymentType === 'card') {
      paymentType = 'debit'; // default card payment type to debit for compatibility with unknown creditOrDebit cards.
    }

    if (state) {
      const { error: stateError } = stateSchema.validate(state);
      if (stateError) {
        this.logger.error({ logID: 23662, referenceNumber, message: stateError.message });
        throw new Error(stateError.message);
      }
    }

    // Handle potential NaN intermediary input values like '5.' as '5.00' or '.' as '0.00'
    baseAmount = formatPotentialNaN(baseAmount);

    // #region VALIDATION
    const { error: baseAmountError } = baseAmountSchema.validate(baseAmount);
    if (baseAmountError) {
      this.logger.error({ logID: 21864, referenceNumber, message: baseAmountError.message });
      throw new Error(baseAmountError.message);
    }

    const { error: optionsError } = feeOptionsSchema.validate(options);
    if (optionsError) {
      this.logger.error({ logID: 21923, referenceNumber, message: optionsError.message });
      throw new Error(optionsError.message);
    }

    const { error: feeConfigError } = feeConfigSchema.validate(feeConfigInput);
    if (feeConfigError) {
      this.logger.error({ logID: 21924, referenceNumber, message: feeConfigError.message });
      throw new Error(feeConfigError.message);
    }
    // #endregion

    const feeConfig = new FeeConfiguration(feeConfigInput, options.surchargeByState, state);

    /** @type {Fee[]} */
    const cashFees = [];

    /** @type {Fee[]} */
    const cardFees = [];

    /** Cash Discount also refers to 'Custom Fee' or 'SVC Fee' */
    /** @type {Fee} */
    const cardUpcharge = { ...feeConfig.cardUpcharge, name: 'cardUpcharge', display: false };
    if (!feeConfig.cardUpcharge.enabled) {
      cardUpcharge.value.calculated = '0.00';
    }
    cardFees.push(cardUpcharge);

    /** @type {Fee} */
    const surcharge = { ...feeConfig.surcharge, name: 'surcharge', display: !!feeConfig.surcharge.enabled };
    if (!surcharge.enabled || paymentType !== 'credit') {
      surcharge.value.calculated = '0.00';
    }
    cardFees.push(surcharge);
    cashFees.push({ ...surcharge, value: { ...surcharge.value, calculated: '0.00' } });

    /** @type {Fee} */
    const lateFee = this._getLateFeeDisplayData(feeConfig.lateFee, options);
    cashFees.push(lateFee);
    cardFees.push(lateFee);

    /** @type {Fee} */
    const tax = { ...feeConfig.tax, name: 'tax', display: !!feeConfig.tax.enabled };
    if (!tax.enabled) {
      tax.value.calculated = '0.00';
    }
    cashFees.push(tax);
    cardFees.push(tax);

    const cashResult = this._calculate(baseAmount, cashFees, referenceNumber, options);

    const cardResult = this._calculate(baseAmount, cardFees, referenceNumber, options);

    if (!cashResult?.fees) {
      const erMsg = 'Cash calculation result is missing data.';
      this.logger.error({ logID: 21955, message: erMsg });
      throw new Error(erMsg);
    }

    if (!cardResult?.fees) {
      const erMsg = 'Card calculation result is missing data.';
      this.logger.error({ logID: 21956, message: erMsg });
      throw new Error(erMsg);
    }

    /** @type {DualPricingViewResult} */
    const result = {
      cash: {
        subTotal: cashResult.baseAmount,
        totalAmount: cashResult.totalAmount,
        fees: cashResult.fees,
      },
      card: {
        subTotal: new Decimal(cardResult.baseAmount)
          .plus(new Decimal(cardResult.fees?.cardUpcharge?.amount ?? '0'))
          .toFixed(2),
        totalAmount: cardResult.totalAmount,
        fees: cardResult.fees,
      },
      baseAmount,
      paymentType,
      options,
      referenceNumber,
    };

    return result;
  }

  /**
   * @description Calculates fees to get fee amounts and total amount based on an array of fees.
   * To be invoked by this.getDualPricingCalculatedFees()
   *
   * NOTE: In the case of 'options.taxLast === true', tax must be the last item in the
   * array. Tax last is the only case in which order matters as fees are otherwise
   * applied to baseAmount.
   *
   * @private
   * @param {string} baseAmount
   * @param {Fee[]} fees
   * @param {string} referenceNumber
   * @param {FeeOptions} [options]
   * @param {PaymentType} [paymentType]
   * @returns {CalculationResult}
   * @throws {Error}
   */
  _calculate(baseAmount, fees, referenceNumber, options = {}, paymentType = '') {
    Decimal.set({ precision: 34, rounding: Decimal.ROUND_HALF_EVEN });

    // #region VALIDATION
    const { error: feeArrayError } = feeArraySchema.validate(fees);
    if (feeArrayError) {
      this.logger.error({
        logID: 21946,
        referenceNumber,
        message: feeArrayError.message,
      });
      throw new Error(feeArrayError.message);
    }

    const { error: baseAmountError } = baseAmountSchema.validate(baseAmount);
    if (baseAmountError) {
      this.logger.error({ logID: 21945, referenceNumber, message: baseAmountError.message });
      throw new Error(baseAmountError.message);
    }

    const { error: refNumError } = referenceNumberSchema.validate(referenceNumber);
    if (refNumError) {
      this.logger.error({ logID: 21866, message: refNumError.message });
      throw new Error(refNumError.message);
    }
    // #endregion

    // Pre-validate, normalize order and decide application base (base vs. running total)
    const { normalizedFees, applyOnPlan, subTotal } = this._validateAndNormalizeFees(
      baseAmount,
      fees,
      options,
      referenceNumber,
    );

    /** @type {Decimal} */
    let dTotal = new Decimal(baseAmount);

    /** @type {Record<string, Fee>} */
    const calculatedFees = {};

    /** @type {Error[]} */
    const errors = [];

    normalizedFees.forEach((fee, i) => {
      try {
        const { error } = feeSchema.validate(fee);
        if (error) throw error;

        const workingAmountMap = {
          runningTotal: dTotal,
          base: new Decimal(baseAmount),
          subTotal: new Decimal(subTotal), // baseAmount plus cardUpcharge, otherwise equal to baseAmount
        };

        // Pick the working amount once according to the precomputed plan
        const workingAmount = workingAmountMap[applyOnPlan[i]]; // 'base' | 'runningTotal | 'subTotal'

        const feeAmount = this._applyFee(workingAmount, fee);
        calculatedFees[fee.name] = {
          enabled: !!fee.enabled,
          name: fee.name,
          type: fee.type,
          value: fee.value,
          amount: feeAmount.toFixed(2),
          display: !!fee.display,
        };

        dTotal = dTotal.plus(feeAmount.toDecimalPlaces(2));
      } catch (e) {
        if (e instanceof Error) {
          errors.push(e);
        }
      }
    });

    if (errors.length > 0) {
      const erMsg = 'One or more fee validation errors have occurred.';
      this.logger.error({
        referenceNumber,
        logID: 21865,
        message: erMsg,
        errors,
      });

      throw new Error(erMsg);
    }

    /** @type {CalculationResult} */
    const result = {
      totalAmount: dTotal.toFixed(2), // Ensures values '15.00' aren't reduced to '15'.
      baseAmount: new Decimal(baseAmount).toFixed(2),
      subTotal: new Decimal(subTotal).toFixed(2),
      fees: calculatedFees,
    };

    // Do we want this to be 'info' level? It would clutter our logs.
    this.logger.debug({ logID: 21867, referenceNumber, message: 'Fee Calculation Result', result });

    return result;
  }

  /**
   * @description Applies a fee to a base amount according to fee type. To be invoked from context
   * of this._calculate() only.
   *
   * @private
   * @param {Decimal} base
   * @param {Fee} fee
   * @returns {Decimal}
   */
  _applyFee(base, fee) {
    if (!fee.value?.calculated || !fee.type) return new Decimal(0);
    if (fee.type === '$') {
      // Convert string currency to Decimal
      return base.isZero() ? new Decimal(0) : new Decimal(fee.value.calculated);
    } else if (fee.type === '%') {
      return base.mul(new Decimal(fee.value.calculated).div(100));
    }
    return new Decimal(0);
  }

  /**
   * @description Validates and normalizes the incoming fees for order-sensitive logic and
   * determines which amount each fee should apply to.
   *
   * @private
   * @param {string} baseAmount
   * @param {Fee[]} fees
   * @param {FeeOptions} options
   * @param {string} referenceNumber
   * @returns {{ normalizedFees: Fee[], applyOnPlan: Array<'base' | 'runningTotal' | 'subTotal'> , subTotal: string}}
   */
  _validateAndNormalizeFees(baseAmount, fees, options, referenceNumber) {
    // Ensure unique fee names up-front
    const nameSet = new Set();
    for (const f of fees) {
      if (nameSet.has(f?.name)) {
        const erMsg = `A fee '${f?.name}' has been applied twice. Fees can only be applied once.`;
        this.logger.error({ logID: 21960, referenceNumber, message: erMsg });
        throw new Error(erMsg);
      }

      nameSet.add(f?.name);
    }

    /** @type {{[K in FeeOutputName]?: number}} */
    const indexMap = {};

    // Create index map for all fees
    const normalizedFees = [...fees];
    if (normalizedFees.length > 0) {
      normalizedFees.forEach((f, idx) => {
        if (f?.name) {
          indexMap[f.name] = idx;
        }
      });
    }

    // Set subtotal to include cardUpcharge if dualpricing and cardUpcharge
    let subTotal = baseAmount;
    if (options?.dualPricing && typeof indexMap?.cardUpcharge === 'number') {
      subTotal = this._applyFee(new Decimal(baseAmount), fees[indexMap.cardUpcharge])
        .plus(new Decimal(baseAmount))
        .toFixed(2);
    }

    // Ensure tax is calculated last if taxLast is true
    if (options?.taxLast && indexMap?.tax) {
      const taxIdx = indexMap.tax;
      if (taxIdx !== normalizedFees.length - 1) {
        // Move tax to end
        const [taxFee] = normalizedFees.splice(taxIdx, 1);
        normalizedFees.push(taxFee);
      }
    }

    // Build application plan
    /** @type {Array<'base' | 'runningTotal' | 'subTotal'>} */
    const applyOnPlan = normalizedFees.map((f) => {
      if (options?.taxLast && f?.name === 'tax') return 'runningTotal';
      if (options?.dualPricing && f?.name === 'tax') return 'runningTotal';
      if (f?.name === 'cardUpcharge') return 'base';
      return 'subTotal';
    });

    return { normalizedFees, applyOnPlan, subTotal };
  }

  /**
   * @description Exposes this._calculate() for testing.
   *
   * @param {string} baseAmount
   * @param {Fee[]} fees
   * @param {string} referenceNumber
   * @param {FeeOptions} [options]
   * @returns {CalculationResult}
   * @throws {Error}
   */
  calculatePublic(baseAmount, fees, referenceNumber, options = {}) {
    return this._calculate(baseAmount, fees, referenceNumber, options);
  }
}

module.exports = FeeService;

/**
 * @typedef {import('../../types/fees.d.ts').PaymentType} PaymentType
 * @typedef {import('../../types/fees.d.ts').FeeType} FeeType
 * @typedef {import('../../types/fees.d.ts').Fee} Fee
 * @typedef {import('../../types/fees.d.ts').FeeInput} FeeInput
 * @typedef {import('../../types/fees.d.ts').FeeName} FeeName
 * @typedef {import('../../types/fees.d.ts').FeeOutputName} FeeOutputName
 * @typedef {import('../../types/fees.d.ts').FeeConfig} FeeConfig
 * @typedef {import('../../types/fees.d.ts').FeeConfigInput} FeeConfigInput
 * @typedef {import('../../types/fees.d.ts').FeeOptions} FeeOptions
 * @typedef {import('../../types/fees.d.ts').SurchargeByStateOptions} SurchargeByStateOptions
 * @typedef {import('../../types/fees.d.ts').FeeProgram} FeeProgram
 * @typedef {import('../../types/fees.d.ts').DualPricingViewResult} DualPricingViewResult
 * @typedef {import('../../types/fees.d.ts').CalculationViewResult} CalculationViewResult
 * @typedef {import('../../types/fees.d.ts').CalculationResult} CalculationResult
 * @typedef {import('../../types/common.d.ts').State} State
 * @typedef {{error: () => void, info: () => void, warn: () => void, debug: () => void}} MockLogger
 */
